import 'primary/events/components/matchs/edit/ModalEditScore.scss';

import { Alert, TextField } from '@mui/material';
import { IMatchRepository } from 'domain/event/match/Match.repository';
import { MatchParticipant } from 'domain/event/match/MatchParticipant';
import { UnpyMatch } from 'domain/event/match/UnpyMatch';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { MatchUpdateFormData } from 'primary/events/components/matchs/edit/MatchEditLigueBR';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import ModalContainer from 'primary/ModalContainer/ModalContainer';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

interface ModalEditScoreMatchBRProps {
  isOpen: boolean;
  handleClose: () => void;
  systemPoint: SystemPointsEnum;
  onValidEdit: () => void;
  participant: MatchParticipant;
  match: UnpyMatch;
  retry: () => void;
}

export type MatchUpdateDetailsFormData = {
  id: number;
  startDate?: string;
};
const validationSchema = (t: UseTranslateReturn) =>
  yup.object({
    id: yup.number().required(t('general.forms.errors.required')),
    startDate: yup.string().nullable(),
    scoreUpdateValidations: yup.object({
      // @ts-ignore
      [yup.number()]: yup.object({
        idMatchParticipant: yup.number().nullable(),
        score: yup.number().nullable(),
      }),
    }),
  });

const useOnSubmit =
  (
    matchRepository: IMatchRepository,
    onSubmitted: () => void,
    setLoadingState: (loadingState: boolean) => void,
    t: UseTranslateReturn,
  ) =>
  (data: MatchUpdateFormData) => {
    setLoadingState(true);
    matchRepository
      .update({
        ...data,
        scoreUpdateValidations: Object.values(data.scoreUpdateValidations).filter(
          (s) => !!s.idMatchParticipant,
        ),
      })
      .then(() => {
        onSubmitted();
        sendEventToastMessage(t('event.matchs.edit.success'), 'success');
      })
      .finally(() => {
        setLoadingState(false);
      });
  };
export const ModalEditScoreParticipantBR: FC<ModalEditScoreMatchBRProps> = ({
  isOpen,
  handleClose,
  systemPoint,
  onValidEdit,
  participant,
  match,
  retry,
}) => {
  const { matchsRepository } = useContextDependency();
  const t = useTranslate();
  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    register,
    getValues,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<MatchUpdateFormData>({
    resolver,
    defaultValues: {
      id: match.id,
      //@ts-ignore
      scoreUpdateValidations: {
        [participant.id]: {
          idMatchParticipant: participant.id,
          score:
            systemPoint === SystemPointsEnum.DEFAULT
              ? !participant.score
                ? null
                : match.participants.length - participant.score
              : participant.score,
        },
      },
    },
  });
  const label =
    systemPoint === SystemPointsEnum.DEFAULT
      ? t('event.matchs.edit.editScore.modal.position')
      : t('event.matchs.edit.editScore.modal.score');
  const [loadingSubmit, setLoadingSubmit] = React.useState(false);
  const onSubmit = useOnSubmit(
    matchsRepository,
    () => {
      onValidEdit();
    },
    setLoadingSubmit,
    t,
  );
  return (
    <ModalContainer
      handleClose={handleClose}
      header={{
        title: t('event.matchs.edit.modal.title'),
      }}
      isOpen={isOpen}
      content={
        <div className={'modal-match-edit'}>
          <form onSubmit={handleSubmit(onSubmit)} className={'-formContainer'}>
            <div className={'-fieldContainer'}>
              <div className={'-details'}>
                <h3>{t('event.matchs.edit.modal.detailsTitle')}</h3>
                <Alert severity="info">
                  {t(`event.matchs.edit.editScore.modal.noticeBR.${systemPoint}`)}
                </Alert>
                <InputWrapper
                  className={'unpy-form__field'}
                  label={label}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  Input={TextField}
                  inputProps={{
                    placeholder: label,
                    variant: 'filled',
                    disabled: isSubmitting,
                    ...register(`scoreUpdateValidations.${participant.id}.score`),
                    type: 'number',
                    min: 0,
                  }}
                />
              </div>
            </div>
            <CoupleButtonValidCancel
              isTypeSubmit
              loading={loadingSubmit}
              onClickCancel={handleClose}
            />
          </form>
        </div>
      }
    />
  );
};
